// Unified WebSocket adapter that works with both Socket.io (local) and native WebSocket (Cloudflare)
import { io, Socket } from 'socket.io-client';
import { getRoomFromBackendUrl } from './roomCode';
import { withHostAuthPayload } from './hostEmit';

export interface WebSocketAdapter {
  on(event: string, callback: (data: any) => void): void;
  emit(event: string, data: any): void;
  off(event: string, callback?: (data: any) => void): void;
  id: string | null;
  connected: boolean;
  /** Room code this socket is routed to (Cloudflare DO), if any. */
  routingRoom: string | null;
  disconnect(): void;
  close(): void; // Alias for disconnect for Socket.io compatibility
}

class SocketIOAdapter implements WebSocketAdapter {
  private socket: Socket;
  private readonly _routingRoom: string | null;

  constructor(url: string) {
    this._routingRoom = getRoomFromBackendUrl(url);
    this.socket = io(url, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5, // Limit attempts - our custom logic handles reconnection
      timeout: 45000, // Match backend connectTimeout
      // Note: pingTimeout is a server-side option only, not needed on client
      // The client automatically adapts to the server's ping/pong settings
      // Keep connection alive even when tab is in background
      forceNew: false,
      // Don't close connection on page unload for hosts
      closeOnBeforeunload: false,
    });
  }

  on(event: string, callback: (data: any) => void): void {
    this.socket.on(event, callback);
  }

  emit(event: string, data: any): void {
    this.socket.emit(event, withHostAuthPayload(event, data));
  }

  off(event: string, callback?: (data: any) => void): void {
    if (callback) {
      this.socket.off(event, callback);
    } else {
      this.socket.off(event);
    }
  }

  get id(): string | null {
    return this.socket.id || null;
  }

  get connected(): boolean {
    return this.socket.connected;
  }

  get routingRoom(): string | null {
    return this._routingRoom;
  }

  disconnect(): void {
    this.socket.disconnect();
  }

  close(): void {
    this.disconnect();
  }
}

class NativeWebSocketAdapter implements WebSocketAdapter {
  private ws: WebSocket | null = null;
  private eventHandlers: Map<string, Set<(data: any) => void>> = new Map();
  private _id: string | null = null;
  private _connected: boolean = false;
  private url: string; // Full URL (may include ?room= for Cloudflare DO routing)
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private _pendingConnect: boolean = false; // Wait for backend to send socket ID
  /** When true, onclose must not auto-reconnect (intentional disconnect / DO swap). */
  private _intentionalClose: boolean = false;

  constructor(url: string) {
    this.url = url;
    this.connect();
  }

  private connect(): void {
    try {
      // Convert http/https to ws/wss; URL may already include ?room= for DO routing
      const wsUrl = this.url.replace(/^http/, 'ws');
      const separator = wsUrl.includes('?') ? '&' : '?';
      const ws = new WebSocket(`${wsUrl}${separator}transport=websocket`);
      
      ws.onopen = () => {
        console.log('Native WebSocket connected - waiting for backend socket ID...');
        this._connected = true;
        this.reconnectAttempts = 0;
        this._pendingConnect = true;
        // DON'T trigger connect event yet - wait for backend to send socket ID
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type && message.data !== undefined) {
            // CRITICAL: If this is the connect message from backend, use ITS socket ID
            if (message.type === 'connect' && message.data?.id && this._pendingConnect) {
              console.log('Received socket ID from backend:', message.data.id);
              this._id = message.data.id;
              this._pendingConnect = false;
              // NOW trigger connect event with backend's socket ID
              this.triggerEvent('connect', { id: this._id });
              return; // Don't process as regular event
            }
            // Handle structured message: { type: 'event', data: {...} }
            this.triggerEvent(message.type, message.data);
          } else {
            // Handle direct event format
            console.warn('Unexpected message format:', message);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error, event.data);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.triggerEvent('error', error);
        // Also emit 'connect_error' for compatibility with Socket.io event names
        this.triggerEvent('connect_error', error);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this._connected = false;
        this.triggerEvent('disconnect', { reason: event.reason || 'Connection closed' });

        // Do not auto-reconnect after intentional close (e.g. reconnectTo DO swap)
        if (this._intentionalClose) {
          return;
        }

        // Attempt to reconnect
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          setTimeout(() => {
            if (this._intentionalClose) return;
            this.connect();
          }, this.reconnectDelay * this.reconnectAttempts);
        }
      };

      this.ws = ws;
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this._connected = false;
    }
  }

  private triggerEvent(event: string, data: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(callback);
  }

  emit(event: string, data: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket is not open. Cannot emit:', event);
      return;
    }

    const payload = withHostAuthPayload(event, data);

    const message = {
      type: event,
      data: payload
    };

    try {
      this.ws.send(JSON.stringify(message));
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
    }
  }

  off(event: string, callback?: (data: any) => void): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      if (callback) {
        handlers.delete(callback);
      } else {
        handlers.clear();
      }
    }
  }

  get id(): string | null {
    return this._id;
  }

  get connected(): boolean {
    return this._connected && this.ws?.readyState === WebSocket.OPEN;
  }

  get routingRoom(): string | null {
    return getRoomFromBackendUrl(this.url);
  }

  disconnect(): void {
    this._intentionalClose = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this._connected = false;
    this.eventHandlers.clear();
  }

  close(): void {
    this.disconnect();
  }
}

/**
 * Builds backend URL with optional room for Durable Object routing (Cloudflare prod).
 * Room is appended as query param so the worker routes the WebSocket to the correct DO.
 */
export function buildBackendUrl(baseUrl: string, room?: string): string {
  if (!room) return baseUrl;
  try {
    const u = new URL(baseUrl);
    u.searchParams.set('room', room);
    return u.toString();
  } catch {
    return baseUrl + (baseUrl.includes('?') ? '&' : '?') + `room=${encodeURIComponent(room)}`;
  }
}

export function createWebSocketAdapter(url: string, room?: string): WebSocketAdapter {
  // Use native WebSocket for Cloudflare Workers (workers.dev), Socket.io for localhost
  const isCloudflare = url.includes('workers.dev') || url.includes('pages.dev');
  const urlWithRoom = room ? buildBackendUrl(url, room) : url;

  if (isCloudflare) {
    console.log('Using native WebSocket adapter for Cloudflare', room ? `(room=${room})` : '');
    return new NativeWebSocketAdapter(urlWithRoom);
  } else {
    console.log('Using Socket.io adapter for local development');
    return new SocketIOAdapter(urlWithRoom);
  }
}

